// Pulse personas and their presets (ENG-3417).
// Spec: docs/specs/pulse.md §2 (persona catalog) and §4 (per-persona rules).
// A persona is what a member is accountable for on Pulse; an admin sets it
// (plan Decision 7). A preset is the code-defined settings a persona carries.
import { DefaultRoleTypes } from "../Roles/DefaultRoles.Interface";

/** The personas, in the spec's order: wave 1, and wave 2's P4, P6 and P8 (ENG-3225). */
export enum PulsePersona {
  /** P1 — sees what's waiting on them and how their conversations are landing. */
  AGENT = "agent",
  /** P2 — knows whether the floor is healthy right now and who or what needs them. */
  TEAM_LEAD = "team-lead",
  /** P3 — gets their site's to-do list and anything a tenant is upset about. */
  SITE_MANAGER = "site-manager",
  /** P4 — ranks their sites by exception, so they know where to spend their week. */
  REGIONAL_MANAGER = "regional-manager",
  /** P5 — sees the three things that matter today and knows the AI handled the rest. */
  OWNER_OPERATOR = "owner-operator",
  /** P6 — sees the score, what's drifting, and which rules to codify. */
  HEAD_OF_OPERATIONS = "head-of-operations",
  /** P8 — sees where demand comes from and where reputation is leaking. */
  MARKETING_REPUTATION = "marketing-reputation",
  /** P9 — knows the score and the one thing to ask their team about. */
  EXECUTIVE = "executive"
}

/** Whether a value is one of Pulse's personas: a member's persona is stored as plain text. */
export function isPulsePersona(value: unknown): value is PulsePersona {
  return Object.values(PulsePersona).some((persona) => persona === value);
}

/** Which outcomes a persona is accountable for (spec §2). */
export enum PulseLens {
  SERVICE = "service",
  REVENUE = "revenue",
  DEMAND_REPUTATION = "demand-reputation",
  OPERATIONS = "operations",
  FINANCE = "finance",
  /** Internal only: Swivl staff's measures, such as Opened Pulse. */
  CUSTOMER_HEALTH = "customer-health"
}

/** How live the page must be; sets the freshness limit (spec §2). */
export enum PulseTempo {
  SHIFT = "shift",
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly"
}

/**
 * How old a saved page may get before it is refreshed, per tempo (spec §7:
 * shift 1 h, daily 3 h, weekly 12 h). No preset is monthly.
 */
export const PULSE_FRESHNESS_MINUTES: Readonly<Record<PulseTempo.SHIFT | PulseTempo.DAILY | PulseTempo.WEEKLY, number>> = {
  [PulseTempo.SHIFT]: 60,
  [PulseTempo.DAILY]: 180,
  [PulseTempo.WEEKLY]: 720
};

/** How much of the Now band shows (spec §4.4): the stat strip and live-by-channel bar, one line, or nothing. */
export type PulseNowBand = "full" | "line" | "off";

export interface PulsePreset {
  persona: PulsePersona;
  label: string;
  lenses: PulseLens[];
  tempo: PulseTempo;
  freshnessMinutes: number;
  /** The Now band's density until the member changes it (spec §5). */
  nowBand: PulseNowBand;
  /** The least the member can collapse it to: a Team Lead's band never collapses (spec §4.4). */
  nowBandFloor: PulseNowBand;
  // LINK apps/bot/src/Pulse/Pulse.Board.ts digestChart
  /** The title over the digest's chart, which the bot draws for the persona (spec §5's templates). */
  digestChartTitle: string;
}

/** "All" for the customer-facing personas never includes the internal customer-health lens. */
const ALL_CUSTOMER_LENSES: PulseLens[] = [PulseLens.SERVICE, PulseLens.REVENUE, PulseLens.DEMAND_REPUTATION, PulseLens.OPERATIONS, PulseLens.FINANCE];

/**
 * The presets, from the spec §3 catalog. Where the spec gives a tempo range
 * ("shift→daily") the preset takes the first value. Which signals a persona
 * sees is declared on each signal, not here, so there is one source. Wave 2's
 * three direct and never take live work, so their Now band starts off
 * (ENG-3225, Mason 6 October).
 */
export const PULSE_PRESETS: Readonly<Record<PulsePersona, PulsePreset>> = {
  [PulsePersona.AGENT]: {
    persona: PulsePersona.AGENT,
    label: "Agent",
    lenses: [PulseLens.SERVICE],
    tempo: PulseTempo.SHIFT,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.SHIFT],
    nowBand: "full",
    nowBandFloor: "line",
    digestChartTitle: "Who handled it, by hour"
  },
  [PulsePersona.TEAM_LEAD]: {
    persona: PulsePersona.TEAM_LEAD,
    label: "Team Lead",
    lenses: [PulseLens.SERVICE],
    tempo: PulseTempo.SHIFT,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.SHIFT],
    nowBand: "full",
    nowBandFloor: "full",
    digestChartTitle: "Who handled it, by hour"
  },
  [PulsePersona.SITE_MANAGER]: {
    persona: PulsePersona.SITE_MANAGER,
    label: "Site Manager",
    lenses: [PulseLens.OPERATIONS, PulseLens.SERVICE],
    tempo: PulseTempo.DAILY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.DAILY],
    nowBand: "line",
    nowBandFloor: "off",
    digestChartTitle: "What tenants talked about"
  },
  [PulsePersona.REGIONAL_MANAGER]: {
    persona: PulsePersona.REGIONAL_MANAGER,
    label: "Regional Manager",
    lenses: [PulseLens.OPERATIONS, PulseLens.REVENUE],
    tempo: PulseTempo.DAILY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.DAILY],
    nowBand: "off",
    nowBandFloor: "off",
    digestChartTitle: "Health by site · 7 days"
  },
  [PulsePersona.OWNER_OPERATOR]: {
    persona: PulsePersona.OWNER_OPERATOR,
    label: "Owner-Operator",
    lenses: ALL_CUSTOMER_LENSES,
    tempo: PulseTempo.DAILY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.DAILY],
    nowBand: "line",
    nowBandFloor: "off",
    digestChartTitle: "Who handled it · 7 days"
  },
  [PulsePersona.HEAD_OF_OPERATIONS]: {
    persona: PulsePersona.HEAD_OF_OPERATIONS,
    label: "Head of Operations",
    lenses: [PulseLens.OPERATIONS, PulseLens.SERVICE],
    tempo: PulseTempo.WEEKLY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.WEEKLY],
    nowBand: "off",
    nowBandFloor: "off",
    digestChartTitle: "Who handled it · 7 days"
  },
  [PulsePersona.MARKETING_REPUTATION]: {
    persona: PulsePersona.MARKETING_REPUTATION,
    label: "Marketing & Reputation",
    lenses: [PulseLens.DEMAND_REPUTATION],
    tempo: PulseTempo.WEEKLY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.WEEKLY],
    nowBand: "off",
    nowBandFloor: "off",
    digestChartTitle: "Average rating · 12 weeks"
  },
  [PulsePersona.EXECUTIVE]: {
    persona: PulsePersona.EXECUTIVE,
    label: "Executive",
    lenses: ALL_CUSTOMER_LENSES,
    tempo: PulseTempo.WEEKLY,
    freshnessMinutes: PULSE_FRESHNESS_MINUTES[PulseTempo.WEEKLY],
    nowBand: "off",
    nowBandFloor: "off",
    digestChartTitle: "Health · 12 weeks"
  }
};

/**
 * The role an invite starts with when the admin picks a persona (Mason,
 * 2026-09-30); the admin can still change it on the invite. A persona is what
 * a member watches on Pulse and a role is what they may do. The two share the
 * word "agent", and this map keeps them apart.
 */
export const PULSE_DEFAULT_ROLE_BY_PERSONA: Readonly<Record<PulsePersona, DefaultRoleTypes>> = {
  [PulsePersona.AGENT]: DefaultRoleTypes.AGENT,
  [PulsePersona.TEAM_LEAD]: DefaultRoleTypes.MANAGER,
  [PulsePersona.SITE_MANAGER]: DefaultRoleTypes.MANAGER,
  [PulsePersona.REGIONAL_MANAGER]: DefaultRoleTypes.MANAGER,
  [PulsePersona.OWNER_OPERATOR]: DefaultRoleTypes.ADMIN,
  [PulsePersona.HEAD_OF_OPERATIONS]: DefaultRoleTypes.ADMIN,
  [PulsePersona.MARKETING_REPUTATION]: DefaultRoleTypes.MANAGER,
  [PulsePersona.EXECUTIVE]: DefaultRoleTypes.ADMIN
};
